import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { authService } from '../services/authService';
import { setGAUser, clearGAUser } from '../utils/analytics';

export interface User {
  id: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  roles?: string[];
  userUuid?: string;
  status?: string; // Added to support provider approval status
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  login: (redirectUri?: string) => void;
  logout: () => void;
  clearError: () => void;
  refreshUser: () => Promise<void>;
  isProvider: () => boolean;
  isProviderUnderReview: () => boolean;
  isProviderActive: () => boolean;
  isAdmin: () => boolean;
}

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_ERROR' }
  | { type: 'LOGOUT' };

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'SET_USER':
      // Keep GA user_id in sync with auth state
      if (action.payload?.userUuid) {
        setGAUser(action.payload.userUuid);
      } else if (!action.payload) {
        clearGAUser();
      }
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        isLoading: false,
        error: null,
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'LOGOUT':
      clearGAUser();
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    default:
      return state;
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const initializeAuth = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      try {
        // Only try to get user if we have a token
        const token = localStorage.getItem('access_token');
        if (token) {
          console.log('Found existing access token, checking authentication...');
          const user = await authService.getCurrentUser();
          dispatch({ type: 'SET_USER', payload: user });
        } else {
          console.log('No access token found, user not authenticated');
          dispatch({ type: 'SET_USER', payload: null });
        }
      } catch (error) {
        console.log('Authentication check failed:', error);
        // Clear any invalid tokens
        localStorage.removeItem('access_token');
        dispatch({ type: 'SET_USER', payload: null });
      }
    };

    // Listen for storage changes (when tokens are added/removed)
    const handleStorageChange = async (e: StorageEvent) => {
      if (e.key === 'access_token') {
        if (e.newValue) {
          // Token was added, refresh user
          try {
            console.log('Access token added, refreshing user...');
            const user = await authService.getCurrentUser();
            dispatch({ type: 'SET_USER', payload: user });
          } catch (error) {
            console.log('Failed to get user after token change:', error);
            dispatch({ type: 'SET_USER', payload: null });
          }
        } else {
          // Token was removed, logout user
          console.log('Access token removed, logging out...');
          dispatch({ type: 'LOGOUT' });
        }
      }
    };

    // Listen for custom auth token events
    const handleAuthTokenChange = async (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail?.token) {
        // Token was added, but don't fetch user info here as it's handled by the callback
        console.log('Auth token changed, user info will be set by callback handler');
      } else {
        // Token was removed, logout user
        console.log('Auth token cleared, logging out...');
        dispatch({ type: 'LOGOUT' });
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authTokenChanged', handleAuthTokenChange);
    initializeAuth();

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authTokenChanged', handleAuthTokenChange);
    };
  }, []);

  const login = (redirectUri?: string) => {
    dispatch({ type: 'CLEAR_ERROR' });
    authService.login(redirectUri);
  };

  const logout = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await authService.logout();
      dispatch({ type: 'LOGOUT' });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Logout failed' });
    }
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const refreshUser = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const user = await authService.getCurrentUser();
      dispatch({ type: 'SET_USER', payload: user });
    } catch (error) {
      console.log('Failed to refresh user:', error);
      dispatch({ type: 'SET_USER', payload: null });
    }
  };

  // Helper functions to check provider status
  const isProvider = () => {
    return state.user?.roles?.includes('PROVIDER') || false;
  };

  const isProviderUnderReview = () => {
    return isProvider() && state.user?.status === 'UNDER_REVIEW';
  };

  const isProviderActive = () => {
    return isProvider() && state.user?.status === 'ACTIVE';
  };

  const isAdmin = () => {
    return state.user?.roles?.includes('ADMIN') || false;
  };

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    clearError,
    refreshUser,
    isProvider,
    isProviderUnderReview,
    isProviderActive,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};